/**
 * @file Wrapper FieldBox dos controles de formulário do EP.
 * @module components/create/fieldbox
 *
 * FieldBox implementa a estrutura visual comum de label, estado required,
 * disabled/readOnly, prefix/suffix, posicionamento em Grid e `input-wrapper`.
 *
 * @see module:components/create/element
 */
import { attachTooltip } from '../define/setEvents.js';


const createFieldHelper = (text) => {
  if (typeof text !== 'string' || text === '') return null;

  const helper = document.createElement('span');
  helper.className = 'helper';
  helper.setAttribute('tabindex', '0');
  helper.setAttribute('aria-label', text);
  attachTooltip(helper, text);
  return helper;
};

/**
 * Posiciona a ajuda contextual depois do controle e de seus adornments.
 * Em inputs com wrapper, o helper vira o último item da linha. Sufixos criados
 * assincronamente com `input.after(...)` continuam sendo inseridos antes dele.
 *
 * @param {HTMLElement} control Controle já anexado ao FieldBox.
 * @param {string} text Conteúdo do tooltip.
 * @returns {HTMLElement|null} Helper criado ou existente.
 */
export const appendFieldHelper = (control, text) => {
  if (!control || typeof text !== 'string' || text === '') return null;

  const parent = control.parentElement;
  const target = parent?.classList?.contains('input-wrapper')
    ? parent
    : (parent?.classList?.contains('fieldbox') ? parent : null);

  if (!target) return null;

  const existing = target.querySelector(':scope > .helper');
  if (existing) return existing;

  const helper = createFieldHelper(text);
  if (!helper) return null;

  target.append(helper);
  return helper;
};

/**
 * Cria o FieldBox ou devolve diretamente o parent quando `hasFieldBox === false`.
 *
 * O método pode retornar o próprio `.fieldbox` ou um `.input-wrapper` interno.
 * Editor, JSON, Textarea e inputs não checkbox/radio/hidden usam wrapper interno.
 * Phone adiciona também a classe `.phone` ao wrapper.
 *
 * `row`, `col`, `rowspan` e `colspan`, quando numéricos, são consumidos pelo
 * FieldBox e removidos de `options` após gerar `grid-area`.
 *
 * @param {Object} options Configuração comum recebida pelo elemento; veja o pipeline de {@link module:components/create/element}.
 * @returns {HTMLElement} FieldBox, InputWrapper ou parent original.
 * @see module:components/create/element
 */
export default (options) => {
    if (options.hasFieldBox === undefined || (options.hasFieldBox !== undefined && options.hasFieldBox !== false)) {
      const parent = (typeof options.parent === 'object') ? (options.parent) : (document.querySelector(options.parent));
      const fieldBox = document.createElement('div');
      parent.append(fieldBox);
      fieldBox.classList.add('fieldbox');

      if (options.fbClass !== undefined && typeof options.fbClass === 'string') {
        const fbClass = options.fbClass.split(' ');
        for (const i in fbClass) {
          fieldBox.classList.add(fbClass[i]);
        }
      }

      if (options.prefix !== undefined) {
        fieldBox.classList.add('pfx');
      }

      if (options.suffix !== undefined) {
        fieldBox.classList.add('sfx');
      }

      if (options.required !== undefined && (options.required === true || options.required === '')) {
        fieldBox.classList.add('required');
      }

      if (options.disabled !== undefined && (options.disabled === true || options.disabled === '')) {
        fieldBox.classList.add('disabled');
      }

      if (options.readOnly !== undefined && (options.readOnly === true || options.readOnly === '')) {
        fieldBox.classList.add('readonly');
      }

      if (options.fbStyle !== undefined && (typeof options.fbStyle === 'string' || typeof options.fbStyle === 'object')) {
        if (typeof options.fbStyle === 'string') {
          const t1 = options.fbStyle.replace(/\s+/g, '').split(';');
          const style = {};
          for (const i in t1) {
            const t2 = t1[i].split(':');

            if (t2[0].length > 0) {
              style[t2[0]] = t2[1];
            }
          }

          options.fbStyle = style;
        }

        for (const key in options.fbStyle) {
          fieldBox.style[key] = options.fbStyle[key];
        }
      }

      if (options.row !== undefined && typeof options.row === 'number' && options.col !== undefined && typeof options.col === 'number') {
        const rowStart = (parseInt(options.row) + 1).toString();
        const rowEnd = (options.rowspan !== undefined) ? (`span ${options.rowspan}`) : ('auto');
        const colStart = (parseInt(options.col) + 1).toString();
        const colEnd = (options.colspan !== undefined) ? (`span ${options.colspan}`) : ('auto');
        fieldBox.style.gridArea = `${rowStart} / ${colStart} / ${rowEnd} / ${colEnd}`;

        delete options.row;
        delete options.rowspan;
        delete options.col;
        delete options.colspan;
      }

      if (options.label !== undefined && options.label !== false) {
        const label = document.createElement('label');
        label.setAttribute('for', options.id);
        label.innerHTML = options.label;
        fieldBox.append(label);
      }

      const noWarappers = ['checkbox', 'hidden', 'radio'];
      const needsInputWrapper = options.element === 'editor'
        || options.element === 'json'
        || options.element === 'textarea'
        || (
          options.element !== 'combo'
          && options.type
          && !noWarappers.includes(options.type)
        );
      if (needsInputWrapper) {
        const wrapper = document.createElement('div');
        fieldBox.append(wrapper);
        wrapper.classList.add('input-wrapper');
        if (options.full !== undefined && options.full === true) {
          wrapper.classList.add('full');
        }

        if(options.element === 'phone'){
          wrapper.classList.add('phone');
        }

        return wrapper;
      } else {
        return fieldBox;
      }
    }
    return (typeof options.parent === 'string') ? (document.querySelector(options.parent)) : (options.parent);
  }
